import { AIEmbeddingType, CompanyProfile, CompanyService, CompanyServiceOptions } from 'models';
import { CompanyHelpers } from 'helpers/CompanyHelpers';
import { TipTapHelpers } from 'helpers/TipTapHelpers';

import { useCollateralItems } from 'components/company-profile/hooks/use-collateral-items';
import { useProductsByCompany } from 'components/company-profile/hooks/use-products-by-company';
import { useReferenceDesigns } from 'components/company-profile/hooks/use-reference-designs';
import { useArticles } from 'hooks/use-articles';

const useShowCompanyInfo = (company: CompanyProfile) => {
    const { referenceDesigns, isLoading: isLoadingDesigns } = useReferenceDesigns(company);
    const { products, isLoading: isLoadingProducts } = useProductsByCompany(company);
    const { articles: caseStudies } = useArticles(company.id, AIEmbeddingType.CASE_STUDY);
    const { items: collateralItems } = useCollateralItems(company);

    const shownLocations = company.locations.filter(CompanyHelpers.hasLocationInformation);
    const shownCaseStudies = caseStudies.filter((article) => article.teaser.image);

    const showHighlights = !!company.highlights?.length;
    const showReferenceDesigns = !!referenceDesigns.length;
    const showAbout = TipTapHelpers.hasContent(company.about);
    const showMission = TipTapHelpers.hasContent(company.mission);
    const showPromos = !!company.promos?.length;
    const showCaseStudies = !!shownCaseStudies.length;
    const showContact = true;
    const showProducts = !!products.length;
    const showContactPeople = !!company.contactPeople.length;
    const showPartners = !!company.partners?.length;
    // Product series are AI-extracted and only a fallback for companies without real products.
    const showProductSeries = !!company.productSeries?.length && !isLoadingProducts && !showProducts;
    const showCollateral = !!collateralItems.length;
    const showServiceInfo = !!company.servicesInfo?.length;
    const showProfilePage = company.mission || company.about || !!shownCaseStudies.length;
    const showEditInfoPage = false;
    const showProductsPage = showProducts;
    const showReferenceDesignsPage = showReferenceDesigns;
    const showPromosPage = showPromos;
    const showCaseStudiesPage = showCaseStudies;

    const filteredServices = CompanyServiceOptions.filter(
        (service) =>
            company.services?.includes(service.value) &&
            ![CompanyService.IN_APP_SUPPORT, CompanyService.OTHER].includes(service.value),
    );
    const shownServices = [...filteredServices.map(({ label }) => label), ...(company.serviceTags ?? [])];

    return {
        showHighlights,
        showReferenceDesigns,
        showAbout,
        showMission,
        showPromos,
        showCaseStudies,
        showContact,
        shownLocations,
        shownCaseStudies,
        showProfilePage,
        showProducts,
        showContactPeople,
        showPartners,
        showProductSeries,
        showCollateral,
        showServiceInfo,
        showEditInfoPage,
        showProductsPage,
        showPromosPage,
        showCaseStudiesPage,
        showReferenceDesignsPage,
        shownServices,
        isLoading: isLoadingDesigns || isLoadingProducts,
    };
};

export { useShowCompanyInfo };
